import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generateBankLogin, generateBankPassword, formatCardNumber } from '@/utils/withdrawal';
import Modal from './Modal';
import type { AdminClient } from '@/mocks/adminClients';

type CredentialsModalProps = {
  client: AdminClient;
  managerEmail: string;
  onSave: (
    clientId: string,
    login: string,
    password: string,
    manager: string,
  ) => Promise<boolean>;
  onSend: (
    clientId: string,
    recipient: string,
    manager: string,
  ) => Promise<{ ok: boolean; to?: string; error?: string }>;
  onClose: () => void;
};

type Feedback = { kind: 'ok' | 'error'; text: string };

/**
 * Gestion des identifiants bancaires d'un client (génération, modification,
 * enregistrement et envoi par e-mail), ouverte depuis la liste des clients.
 */
export default function CredentialsModal({
  client,
  managerEmail,
  onSave,
  onSend,
  onClose,
}: CredentialsModalProps) {
  const { t } = useTranslation();
  const [login, setLogin] = useState(client.bankLogin || generateBankLogin(client.accessCode));
  const [password, setPassword] = useState(client.bankPassword || generateBankPassword());
  const [showPassword, setShowPassword] = useState(false);
  const [recipient, setRecipient] = useState(client.email || '');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const handleRegenerate = () => {
    setLogin(generateBankLogin(client.accessCode));
    setPassword(generateBankPassword());
    setShowPassword(true);
    setFeedback(null);
  };

  const handleSave = async () => {
    if (!login.trim() || !password.trim()) {
      setFeedback({ kind: 'error', text: t('admin.credentials.loginRequired') });
      return;
    }
    setSaving(true);
    const ok = await onSave(client.id, login, password, managerEmail);
    setSaving(false);
    setFeedback(
      ok
        ? { kind: 'ok', text: t('admin.credentials.saved') }
        : { kind: 'error', text: t('common.error') },
    );
  };

  const handleSend = async () => {
    if (!login.trim() || !password.trim()) {
      setFeedback({ kind: 'error', text: t('admin.credentials.loginRequired') });
      return;
    }
    if (!recipient.trim()) {
      setFeedback({ kind: 'error', text: t('admin.credentials.recipientRequired') });
      return;
    }

    setSending(true);
    // On enregistre d'abord les identifiants affichés, puis on les transmet.
    const saved = await onSave(client.id, login, password, managerEmail);
    if (!saved) {
      setSending(false);
      setFeedback({ kind: 'error', text: t('common.error') });
      return;
    }

    const result = await onSend(client.id, recipient, managerEmail);
    setSending(false);
    setFeedback(
      result.ok
        ? { kind: 'ok', text: `${t('admin.credentials.sendDone')} ${result.to ?? recipient}`.trim() }
        : { kind: 'error', text: `${t('admin.credentials.sendError')} ${result.error ?? ''}`.trim() },
    );
  };

  return (
    <Modal
      title={t('admin.credentials.modalTitle')}
      subtitle={`${t('admin.credentials.modalSubtitle')} ${client.accessCode} · ${client.fullName}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-xs text-secondary-900">
          <i className="ri-information-line mt-0.5"></i>
          <span>{t('admin.credentials.genHint')}</span>
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
            {t('admin.credentials.loginLabel')}
          </label>
          <input
            type="text"
            value={login}
            onChange={(event) => {
              setLogin(event.target.value);
              setFeedback(null);
            }}
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 font-mono text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
            {t('admin.credentials.passwordLabel')}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setFeedback(null);
              }}
              className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 pr-11 font-mono text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? t('bankAuth.hidePassword') : t('bankAuth.showPassword')}
              className="absolute right-3 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center text-foreground-400 transition-colors hover:text-foreground-700"
            >
              <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-lg`}></i>
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRegenerate}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
        >
          <i className="ri-refresh-line"></i>
          {t('admin.credentials.regenerate')}
        </button>

        {client.cardNumber && (
          <div className="rounded-md border border-background-200 bg-background-100 p-3.5">
            <p className="mb-2.5 flex items-center gap-2 text-sm font-semibold text-foreground-800">
              <i className="ri-bank-card-2-line text-base text-accent-700"></i>
              {t('admin.credentials.cardTitle')}
            </p>
            <div className="rounded-md border border-dashed border-accent-300 bg-accent-50 px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-accent-800">
                  {client.cardBrand}
                </span>
                <i className="ri-bank-card-line text-lg text-accent-700"></i>
              </div>
              <p className="mt-2 font-mono text-base font-semibold tracking-widest text-foreground-950">
                {formatCardNumber(client.cardNumber)}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-foreground-600">
                <span>
                  {t('admin.credentials.cardExpiry')} :{' '}
                  <strong className="text-foreground-900">{client.cardExpiry}</strong>
                </span>
                <span>
                  CVV : <strong className="text-foreground-900">{client.cardCvv}</strong>
                </span>
              </div>
            </div>
            <p className="mt-2 text-xs text-foreground-500">{t('admin.credentials.cardHint')}</p>
          </div>
        )}

        <div className="rounded-md border border-background-200 bg-background-100 p-3.5">
          <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
            {t('admin.credentials.sendLabel')}
          </label>
          <input
            type="email"
            value={recipient}
            onChange={(event) => {
              setRecipient(event.target.value);
              setFeedback(null);
            }}
            placeholder="client@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
          <p className="mt-2 text-xs text-foreground-500">{t('admin.credentials.sendHint')}</p>
        </div>

        {feedback && (
          <div
            className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${
              feedback.kind === 'ok'
                ? 'border-primary-200 bg-primary-50 text-primary-900'
                : 'border-accent-200 bg-accent-50 text-accent-900'
            }`}
          >
            <i
              className={`mt-0.5 ${
                feedback.kind === 'ok' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
              }`}
            ></i>
            <span>{feedback.text}</span>
          </div>
        )}

        <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || sending}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <i className="ri-loader-4-line animate-spin"></i>}
            {t('admin.credentials.save')}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={saving || sending}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
          >
            <i className={sending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
            {sending ? t('admin.credentials.sending') : t('admin.credentials.send')}
          </button>
        </div>
      </div>
    </Modal>
  );
}